import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Funnel } from 'lucide-react';
import { Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import {
  ATTENDANCE_COLUMN_STYLES,
  useAttendanceColumnLabel,
} from '../constants/attendance-columns';
import { AttendanceCard } from './AttendanceCard';
import type { AttendanceColumnData } from '../hooks/useAttendances';

export interface AttendanceColumnProps {
  agencyId: string;
  column: AttendanceColumnData;
  /** Receives the attendance dropped here. Absent disables the target. */
  onDropAttendance?: (dealId: string, fromStage?: string) => void;
  /**
   * How many cards to show before folding the rest behind "mostrar
   * mais". Absent shows every card the column holds.
   *
   * Only the out-of-play columns pass it. Perdido and Pausados grow
   * without bound and are read rarely, so at full height they pushed the
   * funnel's own columns off the screen -- a board dominated by the work
   * nobody is doing.
   */
  previewLimit?: number;
}

/**
 * Empty-column placeholder: an icon in a tinted circle above the label,
 * in the same shape the visits board uses for its empty columns.
 */
function EmptyColumnState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-(--space-2) rounded-(--radius-md) border border-dashed border-(--border-subtle) bg-(--surface-secondary) p-(--space-6)">
      <div
        className="flex h-10 w-10 items-center justify-center rounded-full"
        style={{ background: 'color-mix(in srgb, var(--text-tertiary) 8%, transparent)' }}
      >
        <Funnel width={18} height={18} style={{ color: 'var(--text-tertiary)', opacity: 0.6 }} />
      </div>
      <Text component="span" className="text-[length:var(--text-meta)] [font-weight:var(--font-medium)] text-(--text-tertiary)">
        {label}
      </Text>
    </div>
  );
}

/**
 * One board column: header with count, then every attendance in it.
 *
 * The column groups stages rather than naming a stored status, so its
 * count is always the number of cards below it.
 */
export function AttendanceColumn({
  agencyId, column, onDropAttendance, previewLimit,
}: AttendanceColumnProps) {
  const [isOver, setIsOver] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const folded = previewLimit != null && !expanded
    && column.attendances.length > previewLimit;
  const visible = folded
    ? column.attendances.slice(0, previewLimit)
    : column.attendances;
  const { t } = useTranslation('features/attendances');
  const columnLabel = useAttendanceColumnLabel();
  const style = ATTENDANCE_COLUMN_STYLES[column.id];

  return (
    <div
      data-testid="attendance-column"
      data-column={column.id}
      data-drop-over={isOver || undefined}
      onDragOver={(event) => {
        if (!onDropAttendance) return;
        // Without this the browser refuses the drop outright, and the
        // column reads as inert no matter what the handler does.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(event) => {
        if (!onDropAttendance) return;
        event.preventDefault();
        setIsOver(false);
        const dealId = event.dataTransfer.getData('text/plain');
        const fromStage = event.dataTransfer.getData('application/x-attendance-stage');
        if (dealId) onDropAttendance(dealId, fromStage || undefined);
      }}
      className={cn(
        'flex flex-col w-full min-w-0',
        'rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-primary) shadow-sm overflow-hidden',
        'transition-colors',
        isOver && 'border-(--seed-base) bg-[color-mix(in_srgb,var(--seed-base)_5%,var(--surface-primary))]',
      )}
    >
      <div className="flex items-center gap-(--space-2) p-(--space-3)">
        <Text
          component="span"
          className="inline-block pb-(--space-1) text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)] text-(--text-primary) whitespace-nowrap"
          style={{ borderBottom: `2px solid ${style.color}` }}
        >
          {columnLabel(column.id)}
        </Text>
        <Text
          component="span"
          className="shrink-0 rounded-(--radius-sm) px-(--space-2) py-(--space-1) text-[length:var(--text-meta)] [font-weight:var(--font-bold)]"
          style={{ background: style.bg, color: style.color }}
        >
          {column.count}
        </Text>
      </div>

      <div className="flex flex-col gap-(--space-2) p-(--space-2) min-h-[80px]">
        {column.attendances.length === 0 ? (
          <EmptyColumnState label={t('column.empty')} />
        ) : (
          visible.map((attendance) => (
            <AttendanceCard
              key={attendance.dealId}
              agencyId={agencyId}
              attendance={attendance}
            />
          ))
        )}

        {folded && (
          <UnstyledButton
            type="button"
            data-testid="attendance-show-more"
            onClick={() => setExpanded(true)}
            className="w-full rounded-(--radius-md) border border-dashed border-(--border-subtle) py-(--space-2) text-[length:var(--text-meta)] text-(--text-secondary) cursor-pointer hover:border-(--seed-base)"
          >
            {t('column.showMore', {
              count: column.attendances.length - (previewLimit ?? 0),
            })}
          </UnstyledButton>
        )}

        {!folded && column.hasMore && (
          <UnstyledButton
            type="button"
            data-testid="attendance-load-more"
            onClick={column.loadMore}
            disabled={column.isLoadingMore}
            className="w-full rounded-(--radius-md) border border-dashed border-(--border-subtle) py-(--space-2) text-[length:var(--text-meta)] text-(--text-secondary) cursor-pointer hover:border-(--seed-base) disabled:cursor-not-allowed disabled:opacity-60"
          >
            {column.isLoadingMore
              ? t('column.loadingMore')
              : t('column.loadMore', {
                  shown: column.attendances.length,
                  total: column.count,
                })}
          </UnstyledButton>
        )}
      </div>
    </div>
  );
}

export default AttendanceColumn;
